import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import { motion } from 'framer-motion';

const MyPosReturn = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [processing, setProcessing] = useState(true);
  const [order, setOrder] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState(null);

  useEffect(() => {
    const checkPayment = async () => {
      const orderId = searchParams.get('orderId');

      if (!orderId) {
        setProcessing(false);
        return;
      }

      try {
        // Fetch order details
        const response = await axios.get(
          `http://localhost:5000/api/orders/${orderId}`,
          { withCredentials: true }
        );

        setOrder(response.data.order);
        setPaymentStatus(response.data.order.payment_status);

        if (response.data.order.payment_status === 'completed') {
          toast.success('Payment successful!');
        } else if (response.data.order.payment_status === 'failed') {
          toast.error('Payment failed');
        } else {
          toast.info('Payment is being processed');
        }
      } catch (err) {
        console.error('Error fetching order:', err);
        toast.error('Failed to load order details');
      } finally {
        setProcessing(false);
      }
    };

    checkPayment();
  }, [searchParams]);

  if (processing) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-gold mx-auto mb-4"></div>
          <p className="text-white text-lg">Checking payment status...</p>
        </div>
      </div>
    );
  }

  const isSuccess = paymentStatus === 'completed';
  const isFailed = paymentStatus === 'failed';

  return (
    <div className="min-h-screen bg-black py-12">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className={`bg-gradient-to-r from-black via-gray-900 to-black p-8 rounded-lg border ${
            isSuccess ? 'border-green-500' : isFailed ? 'border-red-500' : 'border-gold'
          }`}
        >
          {isSuccess && (
            <>
              <div className="text-center mb-8">
                <div className="text-green-500 text-6xl mb-4">✓</div>
                <h1 className="heading-metallic text-3xl mb-2">Payment Successful!</h1>
                <p className="text-gray-400">Thank you for your order</p>
              </div>

              {order && (
                <div className="border-t border-gray-700 pt-6">
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <div>
                      <p className="text-sm text-gray-400">Order Number</p>
                      <p className="text-lg font-bold text-white">{order.order_number}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-400">Total Amount</p>
                      <p className="text-lg font-bold text-gold">{parseFloat(order.total).toFixed(2)} EUR</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-400">Payment Method</p>
                      <p className="text-lg text-white capitalize">{order.payment_method}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-400">Order Status</p>
                      <p className="text-lg text-white capitalize">{order.status}</p>
                    </div>
                  </div>

                  <div className="bg-black p-4 rounded-lg mb-6">
                    <h3 className="heading-metallic-soft text-lg mb-3">Order Items</h3>
                    <div className="space-y-2">
                      {order.items?.map((item, index) => (
                        <div key={index} className="flex justify-between text-sm">
                          <span className="text-gray-300">
                            {item.name} × {item.quantity}
                          </span>
                          <span className="text-white">
                            {(parseFloat(item.price) * item.quantity).toFixed(2)} EUR
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="flex gap-4">
                    <button
                      onClick={() => navigate('/orders')}
                      className="btn-primary flex-1"
                    >
                      View All Orders
                    </button>
                    <button
                      onClick={() => navigate('/products')}
                      className="btn-secondary flex-1"
                    >
                      Continue Shopping
                    </button>
                  </div>
                </div>
              )}
            </>
          )}

          {isFailed && (
            <div className="text-center">
              <div className="text-red-500 text-6xl mb-4">✕</div>
              <h1 className="text-3xl font-bold text-white mb-2">Payment Failed</h1>
              <p className="text-gray-400 mb-6">
                Your payment could not be processed. Please try again.
              </p>
              <button
                onClick={() => navigate('/checkout')}
                className="btn-primary"
              >
                Try Again
              </button>
            </div>
          )}

          {!isSuccess && !isFailed && (
            <div className="text-center">
              <div className="text-gold text-5xl mb-4">⏱</div>
              <h1 className="text-3xl font-bold text-white mb-2">Payment Processing</h1>
              <p className="text-gray-400 mb-6">
                Your payment is being processed. Please check your order status later.
              </p>
              <button
                onClick={() => navigate('/orders')}
                className="btn-primary"
              >
                View Orders
              </button>
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default MyPosReturn;
